import React, { useEffect, useState } from 'react';

interface CommunityTermsProps {
  mode: 'onboarding' | 'profile' | 'modal';
  onAccept?: (acceptedTerms: string[]) => void;
  onClose?: () => void;
  showOnlyRequired?: boolean;
  initialAcceptedTerms?: string[];
}

const PLATFORM_TERMS = [
  {
    id: 'clean-mind',
    title: 'SuperKonnected is een Clean Mind platform.',
    description:
      'We bouwen aan een high-performance omgeving waarin rust, focus en verbinding centraal staan. Geen ruis, drama of afleiding – alleen échte groei.'
  },
  {
    id: 'builders',
    title: 'Ik ben hier gekomen om te helpen en samen te bouwen.',
    description:
      'Iedereen brengt waarde: inspiratie, kennis, aandacht, een vraag of een kans. We zijn mede-creators en versterken elkaars traject.'
  },
  {
    id: 'no-negativity',
    title: 'Ik deel geen negatieve informatie.',
    description:
      'Geen nieuws over wat er misgaat, geen geroddel over andere ondernemers en geen blame, shame of sabotage. Vertrouwen en verantwoordelijkheid zijn de norm.'
  },
  {
    id: 'build-each-other',
    title: 'We vallen elkaar niet aan, maar bouwen elkaar op.',
    description:
      'Feedback is welkom, zolang het respectvol is en bedoeld om te laten groeien. Veiligheid en vertrouwen gaan altijd voor.'
  },
  {
    id: 'energy-ownership',
    title: 'Ik ben verantwoordelijk voor mijn energie.',
    description:
      'We brengen focus, positiviteit en eigenaarschap. Neem pauze als je uit balans bent, communiceer helder en voed de groep met waarde.'
  },
  {
    id: 'stay-pure',
    title: 'Wat we hier bouwen, blijft zuiver.',
    description:
      'Geen clickbait, gekopieerde content of spam. Vertrouwen en verbinding zijn geen marketingtrucjes – we delen wat echt helpt.'
  },
  {
    id: 'respect-pace',
    title: 'Ik respecteer ieders proces en tempo.',
    description:
      'Er is ruimte voor beginners én gevorderden, zolang de intentie zuiver is. Groei hoeft niet synchroon te lopen.'
  }
];

const CommunityTerms: React.FC<CommunityTermsProps> = ({
  mode,
  onAccept,
  onClose,
  showOnlyRequired = false,
  initialAcceptedTerms = []
}) => {
  const [acceptedTerms, setAcceptedTerms] = useState<string[]>([]);
  const totalTerms = PLATFORM_TERMS.length;

  useEffect(() => {
    const validTermIds = new Set(PLATFORM_TERMS.map((term) => term.id));
    setAcceptedTerms(initialAcceptedTerms.filter((termId) => validTermIds.has(termId)));
  }, [initialAcceptedTerms]);

  const handleTermToggle = (termId: string) => {
    setAcceptedTerms(prev => 
      prev.includes(termId) 
        ? prev.filter(id => id !== termId)
        : [...prev, termId]
    );
  };

  const handleAcceptAll = () => {
    const allRequiredTerms = PLATFORM_TERMS.map(term => term.id);
    setAcceptedTerms(allRequiredTerms);
  };

  const handleSubmit = () => {
    if (onAccept) {
      onAccept(acceptedTerms);
    }
  };

  const allRequiredAccepted = PLATFORM_TERMS.every(term => acceptedTerms.includes(term.id));

  const renderContent = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">📜</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          SuperKonnected Platform Voorwaarden
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Dit zijn de Clean Mind afspraken die ons platform zuiver en high-performance houden. Lees ze door en bevestig ze als je je erin herkent.
        </p>
      </div>

      {/* No category tabs in new version */}

      {/* Terms List */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {PLATFORM_TERMS.map((term, index) => (
          <div
            key={term.id}
            className="p-4 rounded-lg border-2 border-blue-100 bg-blue-50"
          >
            <div className="flex items-start space-x-3">
              {mode === 'onboarding' && (
                <input
                  type="checkbox"
                  id={term.id}
                  checked={acceptedTerms.includes(term.id)}
                  onChange={() => handleTermToggle(term.id)}
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                />
              )}
              <div className="flex-1 space-y-2">
                <div className="flex items-start gap-3">
                  <span className="text-lg font-semibold text-blue-600">{index + 1}.</span>
                  <h3 className="font-semibold text-gray-900">{term.title}</h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {term.description}
                </p>
                <span className="inline-flex px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium">
                  Verplicht
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Accept All Button (onboarding only) */}
      {mode === 'onboarding' && (
        <div className="text-center">
          <button
            onClick={handleAcceptAll}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            ✅ Alle verplichte voorwaarden accepteren
          </button>
        </div>
      )}

      {/* Progress Indicator (onboarding only) */}
      {mode === 'onboarding' && (
        <div className="text-center">
          <div className="text-sm text-gray-600 mb-2">
            {acceptedTerms.length} van {totalTerms} voorwaarden geaccepteerd
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${(acceptedTerms.length / totalTerms) * 100}%`
              }}
            />
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {mode === 'onboarding' && (
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleSubmit}
            disabled={!allRequiredAccepted}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {allRequiredAccepted ? '✅ Voorwaarden Accepteren' : '❌ Niet alle voorwaarden geaccepteerd'}
          </button>
        </div>
      )}

      {mode === 'modal' && (
        <div className="text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Sluiten
          </button>
        </div>
      )}
    </div>
  );

  // Different layouts based on mode
  if (mode === 'onboarding') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        {renderContent()}
      </div>
    );
  }

  if (mode === 'modal') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
          {renderContent()}
        </div>
      </div>
    );
  }

  // Profile mode
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {renderContent()}
    </div>
  );
};

export default CommunityTerms;
